import React from 'react';
import { cx } from '../../ui/classNames';
import { fillTargetRect } from './fillDragGeometry';

export const fillHandleClasses = Object.freeze({
    root: 'watchlist-fill-handle',
    handle: 'watchlist-fill-handle__handle',
    target: 'watchlist-fill-handle__target',
    badge: 'watchlist-fill-handle__badge',
});

export const FILL_HANDLE_LABEL = 'Drag to fill. Double-click to fill to the end.';

const c = fillHandleClasses;
// The visible square is 8px with a 1px rim; its invisible hit area is 12px
// (32px on touch screens), centred on it.
const BADGE_INSET = 4;
const HIGH_CONTRAST = '[html[data-theme^=highContrast]_&]';
const ROOT_CLASS_NAME = cx(
    c.root,
    // Above the body rows and their pinned cells, below the sticky header;
    // clipped so the handle never widens the scroll area at the last row or
    // column.
    'pointer-events-none absolute inset-0 z-[4] [overflow:clip]',
);
// The rows a drag would fill: a dashed accent outline over a light accent
// tint (none in the high-contrast themes).
const TARGET_CLASS_NAME = cx(
    c.target,
    'absolute box-border border-2 border-dashed border-accent bg-accent/[0.08] dark:bg-accent/[0.16]',
    `${HIGH_CONTRAST}:bg-transparent`,
);
// "Fill 3 rows" in the accent with MUI's contrast text for it (dark text on
// the two light-blue dark accents), rimmed in the cell colour (the text
// colour in the high-contrast themes).
const BADGE_CLASS_NAME = cx(
    c.badge,
    'absolute -translate-y-1/2 whitespace-nowrap rounded border border-solid border-ui-surface bg-accent px-1.5 py-px',
    'text-[11px] font-semibold leading-4 text-white [html[data-theme=dark]_&]:text-black/[0.87]',
    `[html[data-theme=highContrastDark]_&]:text-black/[0.87] ${HIGH_CONTRAST}:border-content-primary`,
);
// The square reads as cut out of the selection border: its rim is the cell
// colour. Touch drags fill instead of panning the grid.
const HANDLE_CLASS_NAME = cx(
    c.handle,
    'pointer-events-auto absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-crosshair touch-none select-none',
    '[-webkit-tap-highlight-color:transparent] [@media(pointer:coarse)]:h-8 [@media(pointer:coarse)]:w-8',
    "before:absolute before:left-1/2 before:top-1/2 before:box-border before:h-2 before:w-2 before:-translate-x-1/2",
    "before:-translate-y-1/2 before:border before:border-solid before:border-ui-surface before:bg-accent before:content-['']",
);

export function fillBadgeText(rowCount) {
    return `Fill ${rowCount.toLocaleString('en-US')} ${rowCount === 1 ? 'row' : 'rows'}`;
}

/**
 * The spreadsheet fill handle, drawn over the grid in content coordinates:
 * render it inside the grid's scrolled content element, which must be
 * `position: relative`, so it scrolls with the rows and clips to them.
 *
 * - selectionRect: { left, top, width, height } of the selection box, in the
 *   same coordinates as the rows (row i's top is headerHeight + i * rowHeight).
 * - handleProps, targetRange: from useFillDrag. While a drag has a target,
 *   a dashed outline covers the target rows across the selected columns, and
 *   a badge on the row under the pointer counts them.
 * - headerHeight, rowHeight: the values given to useFillDrag.
 *
 * Keyboard users fill with Cmd/Ctrl+D, so the handle stays out of the tab
 * order.
 */
export default function FillHandle({
    selectionRect,
    handleProps,
    targetRange = null,
    headerHeight,
    rowHeight,
}) {
    if (!selectionRect) return null;
    const target = fillTargetRect({ targetRange, selectionRect, headerHeight, rowHeight });
    // The cursor sits near the right edge, where the handle was grabbed, so
    // the badge goes at the left of the row under the pointer.
    const badgeTop = target && (targetRange.direction === 'up'
        ? target.top + (rowHeight / 2)
        : target.top + target.height - (rowHeight / 2));

    return (
        <div className={ROOT_CLASS_NAME}>
            {target && (
                <>
                    <div aria-hidden="true" className={TARGET_CLASS_NAME} style={target} />
                    <div
                        aria-hidden="true"
                        className={BADGE_CLASS_NAME}
                        style={{ left: target.left + BADGE_INSET, top: badgeTop }}
                    >
                        {fillBadgeText(targetRange.endIndex - targetRange.startIndex + 1)}
                    </div>
                </>
            )}
            <div
                {...handleProps}
                role="button"
                aria-label={FILL_HANDLE_LABEL}
                className={HANDLE_CLASS_NAME}
                style={{
                    left: selectionRect.left + selectionRect.width,
                    top: selectionRect.top + selectionRect.height,
                }}
            />
        </div>
    );
}
